/**
 * Sun Direction Layer Hook
 *
 * Displays three directional lines from map center showing:
 * - Sunrise direction (orange)
 * - Sunset direction (orange-red)
 * - Current sun position (gold)
 *
 * Only active when sun-direction layer is enabled
 */

import { useEffect } from 'react'
import mapboxgl from 'mapbox-gl'
import SunCalc from 'suncalc'
import { getLayerConfig } from '../../../../molecules/AnalysisMap/config'

interface UseSunDirectionLayerProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  styleLoadedCount: number
  otherMapOptions: any
}

const SOURCE_ID = 'sun-direction-source'
const LAYER_ID = 'sun-direction-layer'

/**
 * Calculate endpoint coordinates for a direction line
 * @param center - Map center coordinates
 * @param azimuth - Sun azimuth in radians (-π to π, N=0, E=π/2, S=±π, W=-π/2)
 * @param altitude - Sun altitude in radians (0 to π/2)
 * @param bounds - Map bounds to determine appropriate line length
 * @returns Endpoint coordinates {lng, lat}
 */
function calculateLineEndpoint(
  center: { lat: number; lng: number },
  azimuth: number,
  altitude: number,
  bounds: mapboxgl.LngLatBounds
): { lng: number; lat: number } {
  // Calculate viewport dimensions
  const viewportWidthDeg = Math.abs(bounds.getEast() - bounds.getWest())
  const viewportHeightDeg = Math.abs(bounds.getNorth() - bounds.getSouth())
  const maxExtent = Math.min(viewportWidthDeg, viewportHeightDeg)

  // Line length as percentage of viewport (35-40%)
  // Adjust based on sun altitude: lower sun = longer line for visibility
  const altitudeFactor = 0.8 + 0.4 * Math.max(0, altitude / (Math.PI / 2))
  const lineLength = maxExtent * 0.35 * altitudeFactor

  // Convert azimuth to endpoint
  // SunCalc azimuth: -π (N) → 0 (S) → π (N), East is -π/2, West is π/2
  // We need to negate the azimuth to get correct direction
  const deltaLng = Math.sin(azimuth) * lineLength
  const deltaLat = -Math.cos(azimuth) * lineLength

  return {
    lng: center.lng + deltaLng,
    lat: center.lat + deltaLat,
  }
}

/**
 * Generate GeoJSON for the three direction lines
 */
function generateDirectionGeoJSON(
  center: { lat: number; lng: number },
  sunPositions: {
    sunrise: { azimuth: number; altitude: number }
    sunset: { azimuth: number; altitude: number }
    current: { azimuth: number; altitude: number }
  },
  bounds: mapboxgl.LngLatBounds
) {
  const centerCoords: [number, number] = [center.lng, center.lat]

  const sunriseEnd = calculateLineEndpoint(
    center,
    sunPositions.sunrise.azimuth,
    Math.max(0, sunPositions.sunrise.altitude),
    bounds
  )
  const sunsetEnd = calculateLineEndpoint(
    center,
    sunPositions.sunset.azimuth,
    Math.max(0, sunPositions.sunset.altitude),
    bounds
  )
  const currentEnd = calculateLineEndpoint(
    center,
    sunPositions.current.azimuth,
    sunPositions.current.altitude,
    bounds
  )

  return {
    type: 'FeatureCollection' as const,
    features: [
      {
        type: 'Feature' as const,
        id: 'sunrise-line',
        geometry: {
          type: 'LineString' as const,
          coordinates: [centerCoords, [sunriseEnd.lng, sunriseEnd.lat]],
        },
        properties: { direction: 'sunrise' },
      },
      {
        type: 'Feature' as const,
        id: 'sunset-line',
        geometry: {
          type: 'LineString' as const,
          coordinates: [centerCoords, [sunsetEnd.lng, sunsetEnd.lat]],
        },
        properties: { direction: 'sunset' },
      },
      {
        type: 'Feature' as const,
        id: 'current-line',
        geometry: {
          type: 'LineString' as const,
          coordinates: [centerCoords, [currentEnd.lng, currentEnd.lat]],
        },
        properties: {
          direction: 'current',
          // Add opacity property for when sun is below horizon
          opacity: sunPositions.current.altitude < 0 ? 0.5 : 0.9,
        },
      },
    ],
  }
}

/**
 * Hook to manage sun direction layer
 */
export const useSunDirectionLayer = ({
  map,
  mapLoaded,
  styleLoadedCount,
  otherMapOptions,
}: UseSunDirectionLayerProps) => {
  // Effect 1: Layer initialization and mode changes
  useEffect(() => {
    if (!map.current || !mapLoaded) {
      return
    }

    const activeSection = (otherMapOptions as any).activeSection
    const isSunlightSection =
      activeSection === 'sunlight' || activeSection === 'sunlight_study'
    const layerStates = (otherMapOptions as any).demographicsLayerStates
    const sunlightConfig = (otherMapOptions as any).sectionData?.sunlightConfig

    // Get layer state
    const layerConfig = getLayerConfig('sun-direction')
    const layerState = layerStates?.['sun-direction'] || {
      enabled: layerConfig?.defaultEnabled !== false,
    }

    // Only show if in sunlight section and layer is enabled
    if (!isSunlightSection || !layerState.enabled) {
      // Remove layer if it exists
      if (map.current.getLayer(LAYER_ID)) {
        map.current.removeLayer(LAYER_ID)
      }
      if (map.current.getSource(SOURCE_ID)) {
        map.current.removeSource(SOURCE_ID)
      }
      return
    }

    // Get current date and map center
    const currentDate = sunlightConfig?.date || new Date()
    const center = map.current.getCenter()
    const bounds = map.current.getBounds()

    // Calculate sun positions
    const sunTimes = SunCalc.getTimes(currentDate, center.lat, center.lng)
    const sunrisePos = SunCalc.getPosition(
      sunTimes.sunrise,
      center.lat,
      center.lng
    )
    const sunsetPos = SunCalc.getPosition(
      sunTimes.sunset,
      center.lat,
      center.lng
    )
    const currentPos = SunCalc.getPosition(currentDate, center.lat, center.lng)

    // Generate GeoJSON
    const geoJson = generateDirectionGeoJSON(
      { lat: center.lat, lng: center.lng },
      {
        sunrise: sunrisePos,
        sunset: sunsetPos,
        current: currentPos,
      },
      bounds
    )

    // Add or update source
    if (!map.current.getSource(SOURCE_ID)) {
      map.current.addSource(SOURCE_ID, {
        type: 'geojson',
        data: geoJson,
      })
    } else {
      // Update existing source data
      const source = map.current.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource
      source.setData(geoJson)
    }

    // Add layer if it doesn't exist
    if (!map.current.getLayer(LAYER_ID)) {
      map.current.addLayer({
        id: LAYER_ID,
        type: 'line',
        source: SOURCE_ID,
        paint: {
          'line-color': [
            'match',
            ['get', 'direction'],
            'sunrise',
            '#ff8c00', // Dark orange
            'sunset',
            '#ff4500', // Orange-red
            'current',
            '#ffd700', // Gold
            '#ffffff', // Fallback
          ],
          'line-width': 5,
          'line-opacity': ['coalesce', ['get', 'opacity'], 0.9],
        },
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
      })
    }

    // Cleanup function - only cleanup when fundamentals change (style, section, layer enable/disable)
    // NOT when just the date/time changes - that's handled by the separate effect below
    return () => {
      if (map.current) {
        if (map.current.getLayer(LAYER_ID)) {
          map.current.removeLayer(LAYER_ID)
        }
        if (map.current.getSource(SOURCE_ID)) {
          map.current.removeSource(SOURCE_ID)
        }
      }
    }
  }, [
    mapLoaded,
    styleLoadedCount,
    otherMapOptions.activeSection,
    // Include stringified layer state to detect changes - but NOT date
    JSON.stringify(otherMapOptions.demographicsLayerStates?.['sun-direction']),
  ])

  // Effect 2: Date/time updates only - runs without cleanup for smooth real-time updates
  useEffect(() => {
    if (!map.current || !mapLoaded) {
      return
    }

    const activeSection = (otherMapOptions as any).activeSection
    const isSunlightSection =
      activeSection === 'sunlight' || activeSection === 'sunlight_study'
    const layerStates = (otherMapOptions as any).demographicsLayerStates
    const sunlightConfig = (otherMapOptions as any).sectionData?.sunlightConfig

    // Get layer state
    const layerConfig = getLayerConfig('sun-direction')
    const layerState = layerStates?.['sun-direction'] || {
      enabled: layerConfig?.defaultEnabled !== false,
    }

    // Only update if layer should be visible
    if (!isSunlightSection || !layerState.enabled) {
      return
    }

    // Check if source exists
    const source = map.current.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource
    if (!source) {
      return
    }

    const currentDate = sunlightConfig?.date || new Date()
    const center = map.current.getCenter()
    const bounds = map.current.getBounds()

    // Calculate sun positions
    const sunTimes = SunCalc.getTimes(currentDate, center.lat, center.lng)
    const sunrisePos = SunCalc.getPosition(
      sunTimes.sunrise,
      center.lat,
      center.lng
    )
    const sunsetPos = SunCalc.getPosition(
      sunTimes.sunset,
      center.lat,
      center.lng
    )
    const currentPos = SunCalc.getPosition(currentDate, center.lat, center.lng)

    // Generate GeoJSON
    const geoJson = generateDirectionGeoJSON(
      { lat: center.lat, lng: center.lng },
      {
        sunrise: sunrisePos,
        sunset: sunsetPos,
        current: currentPos,
      },
      bounds
    )

    try {
      // Update source data only - this is a lightweight operation
      source.setData(geoJson)
    } catch (error) {
      console.warn('[SunDirection] Error updating source data:', error)
    }

    // NO cleanup - we want the layer to persist
  }, [
    mapLoaded,
    // Only depend on date changes - this makes updates smooth without recreation
    otherMapOptions.sectionData?.sunlightConfig?.date?.getTime(),
  ])
}
